/**
 * DBN Reporting — Institutional Grade Report Engine
 */
import React, { useState, useEffect } from "react";
import { dbnApi, PageHeader, SectionHeader, Badge, LoadingState, ProgressBar, TrafficLight, Btn } from "./dbnUtils";
import { FileText, Download, CheckCircle, AlertTriangle } from "lucide-react";

const FORMAT_LABELS = { pdf:"PDF", excel:"Excel", word:"Word", ppt:"PowerPoint" };
const FORMAT_ICONS  = { pdf:"📄", excel:"📊", word:"📝", ppt:"📊" };
const FORMAT_COLORS = { pdf:"#ef4444", excel:"#22c55e", word:"#3b82f6", ppt:"#f97316" };

function ReportCard({ report, onDownload }) {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (fmt) => {
    setDownloading(fmt);
    await onDownload(report.id, fmt);
    setTimeout(() => setDownloading(null), 2000);
  };

  const statusColor = { Ready:"#22c55e", Published:"#3b82f6", Draft:"#f59e0b" };

  return (
    <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:"14px 16px" }}
      data-testid={`report-${report.id}`}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
        <div style={{ flex:1 }}>
          <p style={{ fontSize:12, fontWeight:700, color:"#f0f9ff", marginBottom:3 }}>{report.name}</p>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <Badge label={report.period} color="#475569" />
            <Badge label={`${report.pages}pp`} color="#334155" />
            <Badge label={report.status} color={statusColor[report.status]||"#475569"} />
          </div>
        </div>
        <FileText size={18} color="#1e3a5f" />
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10 }}>
        {(report.formats || []).map(fmt => (
          <button key={fmt} onClick={() => handleDownload(fmt)}
            data-testid={`download-${report.id}-${fmt}`}
            style={{
              display:"flex", alignItems:"center", gap:4, padding:"4px 10px",
              borderRadius:7, fontSize:9, fontWeight:700, cursor:"pointer",
              background:downloading===fmt?"rgba(52,211,153,0.15)":"transparent",
              border:`1px solid ${FORMAT_COLORS[fmt]||"#475569"}`,
              color:FORMAT_COLORS[fmt]||"#475569",
              transition:"all .15s"
            }}>
            <span>{FORMAT_ICONS[fmt]}</span>
            {downloading===fmt ? "Generating…" : `Download ${FORMAT_LABELS[fmt]}`}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DBNReporting() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("library");

  useEffect(() => {
    dbnApi.reporting().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDownload = (id, fmt) => {
    const url = dbnApi.reportDownload(id, fmt);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.click();
    return Promise.resolve();
  };

  if (loading) return <LoadingState />;
  const r = data?.readiness || {};
  const kpi = data?.portfolio_kpis || {};

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Reporting & Disclosure"
        subtitle="IFRS S2 readiness · EIB reporting · DFI impact framework · Executive report generation">
      </PageHeader>

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* Tabs */}
        <div style={{ display:"flex", gap:2, marginBottom:16, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content" }}>
          {[
            {key:"library",   label:"Report Library"},
            {key:"readiness", label:"Framework Readiness"},
            {key:"ifrs",      label:"IFRS S2 Pillars"},
            {key:"impact",    label:"Impact KPIs"},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background: tab===t.key?"rgba(52,211,153,0.15)":"transparent",
                color: tab===t.key?"#34d399":"#475569" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Report Library */}
        {tab === "library" && (
          <>
            <p style={{ fontSize:10, color:"#475569", marginBottom:14 }}>
              Click any format button to generate and download the report. PDF reports are produced via WeasyPrint.
              Excel, Word and PowerPoint are fully formatted institutional documents.
            </p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {(data?.reports_available||[]).map(report => (
                <ReportCard key={report.id} report={report} onDownload={handleDownload} />
              ))}
            </div>
          </>
        )}

        {/* Framework Readiness */}
        {tab === "readiness" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Reporting Status Overview" sub="Key deadlines and completion status" />
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {[
                  { label:"EIB Facility Reporting", data:r.eib_reporting },
                  { label:"IFRS S1 — Sustainability Disclosures", data:r.ifrs_s1 },
                  { label:"IFRS S2 — Climate Disclosures", data:r.ifrs_s2 },
                  { label:"FRC Nigeria Guidance", data:r.frc_nigeria },
                  { label:"Nigeria Sustainable Banking", data:r.sustainable_banking },
                  { label:"DFI Impact Framework", data:r.dfi_impact },
                  { label:"Assurance / Third-Party Review", data:r.assurance },
                ].filter(x=>x.data).map(x => (
                  <div key={x.label} style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:10, color:"#94a3b8" }}>{x.label}</span>
                      <TrafficLight status={x.data.status} />
                    </div>
                    <ProgressBar value={x.data.pct} color="#34d399" height={4} showPct={true} />
                    <p style={{ fontSize:8, color:"#334155", marginTop:3 }}>Next due: {x.data.next_due}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Reporting Frameworks" sub="Applicability and completion status" />
              <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                {(data?.frameworks||[]).map(f => (
                  <div key={f.id} style={{ display:"flex", alignItems:"center", gap:10, background:"#060f1e", borderRadius:8, padding:"8px 10px" }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>{f.name}</p>
                      <p style={{ fontSize:8, color:"#334155" }}>{f.applicability}</p>
                    </div>
                    <div style={{ width:100 }}>
                      <ProgressBar value={f.completion} color={f.completion>=75?"#34d399":f.completion>=50?"#f59e0b":"#ef4444"} height={4} showPct={true} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* IFRS S2 Pillars */}
        {tab === "ifrs" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {Object.entries(data?.ifrs_s2_pillars||{}).map(([pillar, info]) => {
              const colors = { governance:"#60a5fa", strategy:"#f59e0b", risk_management:"#a78bfa", metrics_targets:"#34d399" };
              const labels = { governance:"Governance", strategy:"Strategy", risk_management:"Risk Management", metrics_targets:"Metrics & Targets" };
              return (
                <div key={pillar} style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                    <p style={{ fontSize:13, fontWeight:800, color:colors[pillar]||"#f0f9ff" }}>{labels[pillar]}</p>
                    <span style={{ fontSize:18, fontWeight:900, color:colors[pillar]||"#f0f9ff" }}>{info.pct}%</span>
                  </div>
                  <ProgressBar value={info.pct} color={colors[pillar]||"#34d399"} height={6} showPct={false} />
                  <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:5 }}>
                    {(info.items||[]).map((item, i) => (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:6, height:6, borderRadius:1,
                          background:i===0?"#22c55e":i===1?"#f59e0b":"#475569", flexShrink:0 }} />
                        <span style={{ fontSize:9, color:"#64748b" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Impact KPIs */}
        {tab === "impact" && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
            {[
              { label:"Total Jobs Created",      value:kpi.total_jobs?.toLocaleString(),    color:"#fbbf24" },
              { label:"Women Beneficiaries",     value:kpi.women_financed?.toLocaleString(),color:"#ec4899" },
              { label:"Youth Beneficiaries",     value:kpi.youth_financed?.toLocaleString(),color:"#a78bfa" },
              { label:"Rural Beneficiaries",     value:kpi.rural_beneficiaries?.toLocaleString(), color:"#60a5fa" },
              { label:"Emissions Avoided tCO2e", value:kpi.emissions_avoided?.toLocaleString(),   color:"#34d399" },
              { label:"Renewable Energy MWh",    value:kpi.energy_mwh?.toLocaleString(),    color:"#0d9488" },
              { label:"Waste Recovered (t)",     value:kpi.waste_recovered?.toLocaleString(),color:"#f97316" },
              { label:"Digital Users Reached",   value:kpi.digital_users?.toLocaleString(), color:"#3b82f6" },
            ].map(x => (
              <div key={x.label} style={{ background:"#0a1628", border:"1px solid #0f2033",
                borderTop:`3px solid ${x.color}`, borderRadius:12, padding:"14px 16px" }}>
                <p style={{ fontSize:20, fontWeight:900, color:x.color, marginBottom:4 }}>{x.value || "—"}</p>
                <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{x.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
